import React, {useCallback, useMemo} from "react";
import moment from "moment";
import {isObject} from "lodash";
import {ConfigProvider, Modal, Spin, Timeline} from "antd";
import {useRequest} from "../../utils/hooks";
import {Images} from "../../images/Images";
import {ServiceIdToName} from "../../consts.js";
import {briefProviderDeclineOptionsToText} from "../../event/DeclineOrderModal/consts.js";
import "./brief-changes-history-modal.css";
import {StringBuilder} from "../../AppUtils.js";

export const BriefChangesHistoryModal = ({visible, onClose, briefId, isAdmin}) => {
    const [brief] = useRequest(
        isAdmin ? `/admin/api/briefs/${briefId}` : `/api/briefs/${briefId}`,
        "POST",
        {
            briefId
        },
        [briefId, isAdmin],
        !!briefId
    );

    const [briefChangesLogs, loadingLogs] = useRequest(
        isAdmin ? `/admin/api/briefChangeLog/${briefId}` : `/api/briefChangeLog/${briefId}`,
        "get",
        null,
        [],
        !!briefId
    );

    const extractFieldValues = useCallback((field, withFieldName = true) => {
        let extractedValues;
        const values = [field.oldValue, field.newValue].filter(Boolean);
        switch (field.fieldId) {
            case "dtstart":
                extractedValues = values.map(val => (val ? moment(val).format("DD/MM/YYYY HH:mm") : null));
                break;
            case "serviceId":
                extractedValues = values.map(val => (val ? ServiceIdToName[val] || val : null));
                break;
            case "providerDeclineReason":
                extractedValues = values.map(val => (val ? briefProviderDeclineOptionsToText[val] || val : null));
                break;
            default:
                extractedValues = values.map(val => (isObject(val) ? JSON.stringify(val) : val));
                break;
        }

        return (
            <div>
                {withFieldName ? `${field.fieldName}: ` : null}
                <span className="brief-changes-history-modal-old-value">{extractedValues[0] ?? "ללא ערך"}</span>
                {!field.oldValue ? null : (
                    <>
                        <span style={{marginRight: "5px"}}>שונה ל-</span>
                        <span className="brief-changes-history-modal-new-value">{extractedValues[1] ?? "ללא ערך"}</span>
                    </>
                )}
            </div>
        );
    }, []);

    const timeLines = useMemo(() => {
        if (!briefChangesLogs || briefChangesLogs.length === 0) {
            return null;
        }

        return briefChangesLogs.map(log => {
            const hasChangedFields = log.changedFields && log.changedFields.length > 0;

            if (!log.title && !hasChangedFields) {
                return null;
            }

            let logTitle;
            if (log.title) {
                logTitle = log.title;
            } else if (hasChangedFields) {
                const multipleFieldsChanged = log.changedFields.length > 1;
                logTitle = multipleFieldsChanged ? "עריכת בריף" : log.changedFields[0].fieldName;
            } else {
                logTitle = "עריכת בריף";
            }

            const getCircleIcon = () => {
                if (log.logLevel === "success") {
                    return <div className="brief-history-success-circle" />;
                }
                return <div className="brief-history-default-circle" />;
            };

            const timeLine = (
                <Timeline.Item key={log.logId} dot={getCircleIcon()}>
                    <div className="budget-history-timeline-item">
                        <span>
                            <u>{logTitle}</u>
                            <span> | </span>
                            <span>
                                {new StringBuilder(log.editor.email, " | ")
                                    .append(moment(log.createdAt).format("DD.MM.YY HH:mm"))
                                    .toString()}
                            </span>
                        </span>
                    </div>
                    {hasChangedFields && (
                        <div className="brief-changes-history-modal-log-body">
                            {log.changedFields.map(changedField => (
                                <div key={changedField.fieldId}>{extractFieldValues(changedField)}</div>
                            ))}
                        </div>
                    )}
                </Timeline.Item>
            );

            return timeLine;
        });
    }, [briefChangesLogs, extractFieldValues]);

    return (
        <Modal
            centered={true}
            width={640}
            className="wb-modal-radius"
            style={{borderRadius: "5px"}}
            destroyOnClose={true}
            open={visible}
            onCancel={onClose}
            footer={null}
            title={null}>
            <ConfigProvider direction="rtl">
                <div className="brief-changes-history-modal">
                    <div className="brief-changes-history-modal-title">
                        {new StringBuilder("היסטוריית בריף").toString()}
                    </div>
                    <div className="simple-modal-break-line" />
                    {loadingLogs ? (
                        <Spin size="large" />
                    ) : timeLines?.length ? (
                        <Timeline>{timeLines}</Timeline>
                    ) : (
                        <div>
                            <i>"הבריף שאין לו עבר, אין לו הווה ועתידו לוט בערפל"</i>
                            <div>להודעה זאת אין עבר</div>
                        </div>
                    )}
                </div>
            </ConfigProvider>
        </Modal>
    );
};
